import type { NavigationType } from 'react-router-dom';

export interface HistoryLocationSnapshot {
  key: string;
  pathname: string;
  search: string;
  hash: string;
}

export interface HistoryLedger {
  scopeId: string;
  entries: HistoryLocationSnapshot[];
  cursor: number;
  canGoBack: boolean;
  canGoForward: boolean;
}

export interface NavigationHistoryContextValue extends HistoryLedger {
  location: HistoryLocationSnapshot;
  action: NavigationType;
}

export interface HistoryLedgerEvent {
  action: NavigationType;
  location: HistoryLocationSnapshot;
}

export function emptyLedger(scopeId: string): HistoryLedger {
  return {
    scopeId,
    entries: [],
    cursor: -1,
    canGoBack: false,
    canGoForward: false,
  };
}

function sameLocation(
  left: HistoryLocationSnapshot,
  right: HistoryLocationSnapshot,
): boolean {
  return left.key === right.key
    && left.pathname === right.pathname
    && left.search === right.search
    && left.hash === right.hash;
}

function ledgerAt(
  scopeId: string,
  entries: HistoryLocationSnapshot[],
  cursor: number,
): HistoryLedger {
  return {
    scopeId,
    entries,
    cursor,
    canGoBack: cursor > 0,
    canGoForward: cursor >= 0 && cursor < entries.length - 1,
  };
}

export function reduceHistoryLedger(
  state: HistoryLedger,
  event: HistoryLedgerEvent,
): HistoryLedger {
  const current = state.entries[state.cursor];
  if (current && sameLocation(current, event.location)) return state;

  switch (event.action) {
    case 'PUSH': {
      const entries = [
        ...state.entries.slice(0, state.cursor + 1),
        event.location,
      ];
      return ledgerAt(state.scopeId, entries, entries.length - 1);
    }
    case 'REPLACE': {
      if (!current) return ledgerAt(state.scopeId, [event.location], 0);
      const entries = [...state.entries];
      entries[state.cursor] = event.location;
      return ledgerAt(state.scopeId, entries, state.cursor);
    }
    case 'POP': {
      const knownIndex = state.entries.findIndex(
        (entry) => entry.key === event.location.key,
      );
      if (knownIndex === -1) return ledgerAt(state.scopeId, [event.location], 0);
      const entries = [...state.entries];
      entries[knownIndex] = event.location;
      return ledgerAt(state.scopeId, entries, knownIndex);
    }
  }

  return state;
}
